import type { WalletDto } from '@/queries/wallet/wallet.schema';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { Button } from '@/components/ui/Button/button';
import { useCurrency } from '@/hooks/useCurrency';
import { useIsMobile } from '@/hooks/useIsMobile';
import { summarizeSCBalances } from '@/libs/summarizeSCBalances';
import openModal from '@/modals/utils/openModal';
import goldCoinShine from '@/public/assets/image/balance/gold-coin-shine.webp';
import sweepCoinShine from '@/public/assets/image/balance/sweep-coin-shine.webp';
import { cn } from '@/utils/cn';
import { formatCentsToCurrencyNoDecimals } from '@/utils/currency.utils';
import BalanceInfoModal from './BalanceInfoModal';

interface Props {
  coins?: WalletDto;
  balancesPopoverClose: () => void;
}
const BalanceCard = ({ coins, balancesPopoverClose }: Props) => {
  const t = useTranslations('header.balance-card');
  const { currency, setCurrency } = useCurrency();
  const [isBalanceInfoOpen, setIsBalanceInfoOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleSelectCurrency = (currency: 'GC' | 'SC') => {
    setCurrency(currency);
    balancesPopoverClose();
  };

  const handleOpenDepositModal = () => {
    openModal('deposit');
    balancesPopoverClose();
  };

  const handleInfoClick = () => {
    if (isMobile) {
      setIsBalanceInfoOpen(true);
    } else {
      openModal('sweep-coin-info');
    }
  };

  return (
    <>
      <div className="flex relative items-center justify-between">
        <h6 className="text-c300 paragraph-s-medium px-2">{t('choose-currency')}</h6>
        <div className="pr-2">
          <button
            type="button"
            className="size-8 flex items-center justify-center rounded-full  bg-black/30 hover:bg-violet-main-900 transition-colors duration-300"
            aria-label="Close"
            onClick={balancesPopoverClose}
          >
            <Icon name="x_close" className="text-primary-3" />
          </button>
        </div>
      </div>
      <div className="flex flex-col mt-4.5 px-2">
        <button
          className={cn(
            'group flex items-center justify-between w-full gap-2 px-2.5 py-2 rounded-[3.125rem] relative transition-all duration-300',
            currency === 'GC' && 'glass-effect',
          )}
          type="button"
          onClick={() => handleSelectCurrency('GC')}
        >
          <div className="flex items-center gap-2">
            <div className="relative size-12 shrink-0 flex items-center justify-center overflow-visible">
              <Image
                src={goldCoinShine}
                alt="Gold Coin Shine"
                width={48}
                height={48}
                aria-hidden
                className={cn(
                  'pointer-events-none -z-1 absolute inset-0 m-auto size-12 object-cover transition-opacity duration-300 opacity-0 group-hover:opacity-100 left-[80%] top-[35%] scale-330',
                  currency === 'GC' && 'opacity-100 left-full top-[21%] scale-400',
                )}
              />
              <Icon name="gold-coin" className={cn('relative z-10', currency === 'GC' ? 'size-12' : 'size-8')} />
            </div>
            <div className="flex flex-col items-start">
              <span className={cn(
                'text-regular text-[0.75rem] leading-4',
                currency === 'GC' ? 'text-white' : 'text-c300',
              )}
              >
                {t('gold-coins')}
              </span>
              <span className={cn('text-xl font-medium leading-[130%]', currency === 'GC' ? 'text-white' : 'text-c300')}>
                {formatCentsToCurrencyNoDecimals(coins?.goldCoin || 0)}
              </span>
            </div>
          </div>
          <Icon name="check-main" className={cn(currency === 'GC' ? 'visible' : 'invisible')} />

        </button>
        <button
          className={cn(
            'group flex items-center mt-2 justify-between w-full gap-2 pl-2.5 pr-[14px] py-2 rounded-[3.125rem] transition-all duration-400',
            currency === 'SC' && 'glass-effect',
          )}
          type="button"
          onClick={() => handleSelectCurrency('SC')}
        >

          <div className="flex items-center gap-2">
            <div className="relative size-12 shrink-0 flex items-center justify-center overflow-visible">
              <Image
                src={sweepCoinShine}
                alt="Sweep Coin Shine"
                width={48}
                height={48}
                aria-hidden
                className={cn(
                  'pointer-events-none absolute inset-0 -z-1 m-auto size-12 object-cover transition-opacity duration-400 opacity-0 group-hover:opacity-100 left-[70%] -top-[15%] scale-300',
                  currency === 'SC' && 'opacity-100 left-full -top-[15%] scale-400',
                )}
              />
              <Icon name="sweep-coin" className={cn('relative z-10', currency === 'SC' ? 'size-12' : 'size-8')} />
            </div>
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-1">
                <span className={cn(
                  'text-regular text-[0.75rem] leading-4',
                  currency === 'SC' ? 'text-white' : 'text-c300',
                )}
                >
                  {t('sweeps-coins')}
                </span>
                <div
                  tabIndex={0}
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInfoClick();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleInfoClick();
                    }
                  }}
                  className="group cursor-pointer transition-colors duration-300"
                >
                  <Icon name="info" className={cn('size-3 transition-colors duration-300 group-hover:text-c300', currency === 'SC' ? 'text-white' : 'text-white/80')} />

                </div>
              </div>
              <span className={cn('text-xl font-medium leading-[130%]', currency === 'SC' ? 'text-white' : 'text-c300')}>
                {formatCentsToCurrencyNoDecimals(summarizeSCBalances(Number(coins?.sweepCoin), Number(coins?.bonusSweepCoin)))}
              </span>
            </div>
          </div>
          <Icon name="check-main" className={cn(currency === 'SC' ? 'visible' : 'invisible')} />
        </button>
      </div>
      <div className="px-2">
        <Button
          variant="primary"
          size="sm"
          className="paragraph-s-medium w-full pl-[6.6563rem] mt-[0.9375rem] pr-[7.1563rem] py-2.5 text-white"
          onClick={handleOpenDepositModal}
        >
          <div className="flex items-center ml-1.25">
            <Icon name="top-up" className="size-6 text-white" />
            <span className="">{t('top-up')}</span>
          </div>
        </Button>
      </div>
      <div className="pointer-events-none absolute -right-36.5 -bottom-22.5 blur-[4rem] w-[17.2087rem] h-[8.4281rem] rounded-[624.9375rem] bg-frame-gradient rotate-[162.88deg]" />
      <BalanceInfoModal open={isBalanceInfoOpen} onOpenChange={setIsBalanceInfoOpen} />
    </>
  );
};

export default BalanceCard;
